// ============================================================================
// File: src/miniapps/Lexburnerchat/BurnerChat.jsx   (2025-07-12)
// 完整源码（最新版）
//  • Destroy ⇒ 立即级联删房间 / 消息 / 参与者
//  • 每条消息 60 s 渐隐 → 1 s 后本地删 + 调用 deleteChatMessage 真删 Firestore
//  • “旧消息复活”问题已根治
// 依赖 firebase.js 中：sendChatMessage · subscribeToChat · deleteChatMessage
//                      watchParticipants · destroyRoom
// ============================================================================
import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import AngelWrapper from "./angel5";

import {
  FaCat, FaDog, FaFrog, FaCrow, FaFish, FaPaperclip,
} from "react-icons/fa";
import Picker from "@emoji-mart/react";
import emojiData from "@emoji-mart/data";
import {
  db,
  auth,
  sendChatMessage,
  subscribeToChat,
  deleteChatMessageWithFile,
  watchParticipants,
  destroyRoom,
  uploadScreenshotFile,
} from "../../firebase";
import {
  collection, doc as docRef, getDoc, setDoc,
  onSnapshot, deleteDoc, serverTimestamp,
} from "firebase/firestore";

import "./burner.css";
import { useAuth } from "../../user/AuthContext";
import LexLoginSignupPopup from '../lex-loginsignup-popup/lex-loginsignup-popup.js';
import '../lex-loginsignup-popup/lex-loginsignup-popup.css';


const icons = [FaCat, FaDog, FaFrog, FaCrow, FaFish];
const hashCode = s => s.split("").reduce((a, c) => (a << 5) - a + c.charCodeAt(0), 0);
const iconFor  = s => icons[Math.abs(hashCode(s)) % icons.length];

export default function BurnerChat() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loginPromptVisible, setLoginPromptVisible] = useState(false);
  const [pendingRoomCreation, setPendingRoomCreation] = useState(null);
  const [params] = useSearchParams();
  const room     = params.get("room");
  const pwdParam = params.get("pwd") || "";

  const [roomName, setRoomName] = useState("");
  const [roomPwd , setRoomPwd ] = useState("");
  const [needPwd , setNeedPwd ] = useState(false);
  const [nameTaken, setNameTaken] = useState(false);

  const [messages, setMessages]  = useState([]);
  const [fadeMap , setFadeMap ]  = useState({});
  const [input   , setInput   ]  = useState("");
  const [copied  , setCopied  ]  = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [participants, setParticipants] = useState(0);

  const endRef    = useRef(null);
  const fileIn    = useRef(null);
  const timers    = useRef({});

  const userId = useRef(
    localStorage.getItem("burnerId") ||
    (() => {
      const id = "burner-" + Math.random().toString(36).slice(2, 6);
      localStorage.setItem("burnerId", id);
      return id;
    })()
  ).current;

  useEffect(() => {
    if (!roomName.trim()) { setNameTaken(false); return; }
    getDoc(docRef(db, "rooms", roomName.trim()))
      .then(snap => {
        const taken = snap.exists() && snap.data()?.active !== false;
        setNameTaken(taken);
      });
  }, [roomName]);

  useEffect(() => {
    if (!room) return;

    (async () => {
      const meta = await getDoc(docRef(db, "rooms", room));
      if (meta.exists()) {
        const { pwd = "", active = true } = meta.data();
        if (!active) { alert("Room was destroyed."); navigate("/burnerchat"); return; }
        if (pwd && pwd !== pwdParam) { alert("Wrong password"); navigate("/burnerchat"); }
      }
    })();

    const pCol = collection(db, "rooms", room, "participants");
    const me   = docRef(pCol, userId);
    setDoc(me, { joinedAt: serverTimestamp() });
    const unsubP = onSnapshot(pCol, s => setParticipants(s.size));
    const unsubWatch = watchParticipants(room);

    return () => { deleteDoc(me); unsubP(); unsubWatch(); };
  }, [room, userId, pwdParam, navigate]);

  useEffect(() => {
    if (!room) return;
    const unsub = subscribeToChat(room, docs => {
      setMessages(docs);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);

      docs.forEach(d => {
        if (timers.current[d.id]) return;
        timers.current[d.id] = setTimeout(() => {
          setFadeMap(f => ({ ...f, [d.id]: true }));
          setTimeout(async () => {
            setMessages(m => m.filter(x => x.id !== d.id));
            await deleteChatMessageWithFile(room, d.id);
            clearTimeout(timers.current[d.id]);
            delete timers.current[d.id];
            setFadeMap(f => { const { [d.id]: _, ...rest } = f; return rest; });
          }, 1000);
        }, 60000);
      });
    });
    return () => { unsub(); Object.values(timers.current).forEach(clearTimeout); };
  }, [room]);

  useEffect(() => {
    if (user && pendingRoomCreation) {
      const { name, pwd } = pendingRoomCreation;
      (async () => {
        await setDoc(docRef(db, "rooms", name), {
          createdAt: serverTimestamp(),
          pwd,
          active: true,
          ttl: null
        }, { merge: true });
        const r = encodeURIComponent(name);
        navigate(`/burnerchat?room=${r}${pwd ? `&pwd=${encodeURIComponent(pwd)}` : ""}`);
      })();
      setPendingRoomCreation(null);
      setLoginPromptVisible(false);
    }
  }, [user, pendingRoomCreation, navigate]);

  const copy = () => {
    navigator.clipboard.writeText(window.location.href)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2500); });
  };

  const send = () => {
    if (!input.trim()) return;
    sendChatMessage(room, { sender: userId, text: input });
    setInput("");
  };

  const onFile = async e => {
    const f = e.target.files[0];
    if (!f) return;
    const uid = auth.currentUser?.uid || userId;
    const downloadUrl = await uploadScreenshotFile(uid, f);
    await sendChatMessage(room, { sender: userId, fileUrl: downloadUrl });
    e.target.value = null;
  };

  if (!room) {
    return (
      <AngelWrapper>
        {loginPromptVisible && !user && (
          <LexLoginSignupPopup
            loginPromptVisible={loginPromptVisible}
            setLoginPromptVisible={setLoginPromptVisible}
          />
        )}

        <Link to="/app" className="appstore-back">← Back</Link>
        <div className="burner-container" style={{ marginTop: "10vh" }}>
          <h2>Create a Burner Chat Room</h2>

          <input value={roomName} placeholder="Enter room name"
                 onChange={e => setRoomName(e.target.value)} />
          {nameTaken && <div style={{ color:"#ff6464", margin:"1rem 0 .5rem", fontSize:".9rem" }}>This room is currently taken</div>}

          <div className="addpwd-row">
            <input type="checkbox" id="addpwd" checked={needPwd}
                   onChange={e => setNeedPwd(e.target.checked)} />
            <label htmlFor="addpwd">Add&nbsp;Password</label>
            {needPwd &&
              <input className="pwd-field" placeholder="Password"
                     value={roomPwd} onChange={e => setRoomPwd(e.target.value)} />}
          </div>

          <button
            disabled={!roomName.trim() || nameTaken || (needPwd && !roomPwd.trim())}
            onClick={async () => {
              if (!user) {
                setPendingRoomCreation({
                  name: roomName.trim(),
                  pwd: needPwd ? roomPwd.trim() : ""
                });
                setLoginPromptVisible(true);
                return;
              }

              await setDoc(docRef(db, "rooms", roomName.trim()), {
                createdAt: serverTimestamp(),
                pwd: needPwd ? roomPwd : "",
                active: true,
                ttl: null
              }, { merge: true });

              const r = encodeURIComponent(roomName.trim());
              navigate(`/burnerchat?room=${r}${needPwd ? `&pwd=${encodeURIComponent(roomPwd)}` : ""}`);
            }}>
            Create Room
          </button>
        </div>
      </AngelWrapper>
    );
  }

  return (
    <AngelWrapper>
      {loginPromptVisible && !user && (
        <LexLoginSignupPopup
          loginPromptVisible={loginPromptVisible}
          setLoginPromptVisible={setLoginPromptVisible}
        />
      )}

      <div className="burner-container" style={{ marginTop: "5vh" }}>
        <div className="burner-header">
          <h2>🔥 Room: {room} ({participants} online)</h2>
          <button className="destroy-btn" onClick={async () => {
            await destroyRoom(room);
            navigate("/burnerchat");
          }}>Destroy</button>

          <div className="burner-share">
            <button onClick={copy} disabled={copied}>
              {copied ? "Copied" : "Share"}
            </button>
          </div>
        </div>

        <div className="burner-messages">
          {messages.map(m => {
            const self = m.sender === userId;
            const Icon = iconFor(m.sender);
            return (
              <div key={m.id} className={`burner-msg ${self ? "self" : "other"}${fadeMap[m.id] ? " fading" : ""}`}>
                <div className="msg-avatar"><Icon /></div>
                <div className="msg-content">
                  {!self && <span className="burner-tag">{m.sender}</span>}
                  {m.text && <span className="burner-text">{m.text}</span>}
                  {m.fileUrl && (
                    <div className="burner-file">
                      <a href={m.fileUrl} target="_blank" rel="noopener noreferrer">
                        {m.fileUrl.split("/").pop()}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>

        <div className="burner-input top-row">
          <button className="file-input-button" onClick={() => fileIn.current.click()} title="Attach file">
            <FaPaperclip />
          </button>
          <input type="file" ref={fileIn} style={{ display: "none" }} onChange={onFile} />

          <button className="emoji-picker-button" onClick={() => setShowEmoji(v => !v)} title="Emoji">😃</button>
          {showEmoji && ReactDOM.createPortal(
            <div className="emoji-picker">
              <Picker data={emojiData} theme="dark"
                      onEmojiSelect={e => { setInput(i => i + e.native); setShowEmoji(false); }} />
            </div>, document.body)}
        </div>

        <div className="burner-input bottom-row">
          <input value={input} placeholder="Type..."
                 onChange={e => setInput(e.target.value)}
                 onKeyDown={e => e.key === "Enter" && send()} />
          <button onClick={send}>Send</button>
        </div>
      </div>
    </AngelWrapper>
  );
}
